import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * MarkdownMessage — renders assistant chat content as formatted Markdown.
 *
 * - GFM (tables, task lists, strikethrough, autolinks) via `remark-gfm`.
 * - Raw HTML in the source is IGNORED (react-markdown default) — safe against XSS from AI output.
 * - Links get target="_blank" rel="noopener noreferrer".
 * - Styling matches the Project RN cream/burgundy theme.
 * - Only used for role === "assistant"; user messages stay plain text.
 */
export default function MarkdownMessage({ children }) {
  return (
    <div className="prn-markdown text-[15px] leading-relaxed text-[#1a1612]" data-testid="assistant-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="text-lg font-black tracking-tight text-[#1a1612] mt-4 mb-2 first:mt-0" {...p} />,
          h2: ({ node, ...p }) => <h2 className="text-base font-bold tracking-tight text-[#1a1612] mt-4 mb-2 first:mt-0" {...p} />,
          h3: ({ node, ...p }) => <h3 className="text-sm font-bold uppercase tracking-widest text-[#6f2a30] mt-3 mb-1.5 first:mt-0" {...p} />,
          h4: ({ node, ...p }) => <h4 className="text-sm font-semibold text-[#1a1612] mt-3 mb-1.5 first:mt-0" {...p} />,
          p: ({ node, ...p }) => <p className="my-2 first:mt-0 last:mb-0" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-bold text-[#1a1612]" {...p} />,
          em: ({ node, ...p }) => <em className="italic" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-2 space-y-1 marker:text-[#6f2a30]" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-2 space-y-1 marker:text-[#6f2a30] marker:font-mono" {...p} />,
          li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
          blockquote: ({ node, ...p }) => (
            <blockquote className="border-l-2 border-[#6f2a30] pl-3 my-3 italic text-[#3a322b] bg-[#f1eee7] py-1.5 rounded-r-sm" {...p} />
          ),
          hr: ({ node, ...p }) => <hr className="my-4 border-t border-[#e4ded2]" {...p} />,
          a: ({ node, ...p }) => (
            <a
              className="text-[#6f2a30] underline underline-offset-2 hover:text-[#582227] break-words"
              target="_blank"
              rel="noopener noreferrer"
              {...p}
            />
          ),
          code: ({ node, inline, className, children, ...p }) => {
            if (inline) {
              return (
                <code className="font-mono text-[13px] px-1 py-0.5 rounded bg-[#f1eee7] text-[#6f2a30]" {...p}>
                  {children}
                </code>
              );
            }
            return (
              <code className={`font-mono text-[13px] ${className || ""}`} {...p}>
                {children}
              </code>
            );
          },
          pre: ({ node, ...p }) => (
            <pre className="bg-[#1a1612] text-[#f1eee7] rounded-sm p-3 my-3 overflow-x-auto text-[12.5px] leading-relaxed" {...p} />
          ),
          table: ({ node, ...p }) => (
            <div className="my-3 overflow-x-auto rounded-sm border border-[#e4ded2]">
              <table className="min-w-full text-[13px] text-left border-collapse" {...p} />
            </div>
          ),
          thead: ({ node, ...p }) => <thead className="bg-[#f1eee7]" {...p} />,
          th: ({ node, ...p }) => <th className="px-3 py-2 font-semibold text-[#1a1612] uppercase text-[10px] tracking-widest border-b border-[#e4ded2]" {...p} />,
          td: ({ node, ...p }) => <td className="px-3 py-2 border-b border-[#e4ded2] align-top" {...p} />,
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
